/** Present persisted session transitions in their original place in the conversation. */
import type { ThreadMessageLike } from '@assistant-ui/react'
import type { BridgeState } from '@web/state/bridge'

const labels: Record<string, string> = {
  created: 'Session saved',
  starting: 'Starting session',
  ready: 'Session ready',
  asleep: 'Session asleep',
  exited: 'Session stopped',
  closed: 'Session closed',
  detached: 'Session disconnected',
  error: 'Session needs attention',
}

const acpConnectionEnded =
  'ACP adapter connection ended; uncertain work will not be repeated'
const disruptedSessionDetail =
  'The connection to this session ended before it could confirm whether your last request finished. Your request was not automatically retried.'

/** Translate the known ACP transport interruption without obscuring other errors. */
function lifecyclePresentation(
  status: string,
  error?: string,
): {
  label: string
  detail?: string
} {
  if (status === 'error' && error?.includes(acpConnectionEnded)) {
    return { label: 'Session Disrupted', detail: disruptedSessionDetail }
  }
  return { label: labels[status]!, detail: error }
}

/** Plain-language detail for a non-sleep transition the control plane explains. */
const reasonDetails: Record<string, string> = {
  // The process hosting the session went away (a runtime replica drained
  // for an upgrade or scale-down, or stopped unexpectedly). Nothing is lost;
  // the next message resumes it after a short start.
  runtime_draining:
    'The runtime hosting this session restarted. Your next message resumes it.',
  recovery:
    'The runtime hosting this session was interrupted. Your next message resumes it.',
}

/**
 * Render a duration in seconds the way a person would say it.
 *
 * Preserve tenths only when the input carries sub-second precision. An integer
 * duration may have come from a source that only recorded whole seconds, so
 * appending `.0` would suggest accuracy the source did not provide.
 */
export function formatDuration(seconds: number): string {
  if (seconds < 10) {
    return Number.isInteger(seconds) ? `${seconds}s` : `${seconds.toFixed(1)}s`
  }
  if (seconds < 60) return `${Math.round(seconds)}s`
  const minutes = Math.floor(seconds / 60)
  const rest = Math.round(seconds - minutes * 60)
  return `${minutes}m ${String(rest).padStart(2, '0')}s`
}

/**
 * Seconds from the start that led to this `ready` transition to the
 * transition itself: the earliest of the contiguous `created`/`starting`
 * events just before it (a launch counts from when the session was saved; a
 * wake counts from its `starting`). Null when either side lacks a timestamp.
 */
export function readyAfterSeconds(
  lifecycle: BridgeState['lifecycle'],
  ready: BridgeState['lifecycle'][number],
): number | null {
  if (ready.status !== 'ready' || typeof ready.timestamp !== 'number')
    return null
  let start: number | null = null
  for (let i = lifecycle.indexOf(ready) - 1; i >= 0; i--) {
    const prior = lifecycle[i]
    if (!prior || !['created', 'starting'].includes(prior.status)) break
    if (typeof prior.timestamp === 'number') start = prior.timestamp
  }
  if (start === null || start > ready.timestamp) return null
  return ready.timestamp - start
}

export function withLifecycleMessages(
  messages: ThreadMessageLike[],
  bridge?: BridgeState,
): ThreadMessageLike[] {
  if (!bridge) return messages
  const events = bridge.lifecycle.filter(
    (event, index, all) =>
      !['created', 'starting'].includes(event.status) &&
      event.status !== all[index - 1]?.status &&
      // Stopping a runtime is the implementation detail of putting an idle
      // session to sleep. The ensuing asleep status is the user-facing result.
      !(event.status === 'exited' && all[index + 1]?.status === 'asleep'),
  )
  const lifecycle: ThreadMessageLike[] = events.map((event, index) => {
    const loading =
      event.status === 'starting' &&
      index === events.length - 1 &&
      bridge.status === 'starting'
    const defaultLabel =
      event.status === 'starting' &&
      events
        .slice(0, index)
        .some((item) =>
          ['asleep', 'exited', 'closed', 'detached', 'error'].includes(
            item.status,
          ),
        )
        ? 'Resuming session'
        : labels[event.status]!
    const presentation = lifecyclePresentation(event.status, event.error)
    const label =
      event.status === 'starting' ? defaultLabel : presentation.label
    const readyAfter =
      event.status === 'ready'
        ? readyAfterSeconds(bridge.lifecycle, event)
        : null
    return {
      id: `lifecycle:${event.seq}`,
      ...(event.timestamp
        ? { createdAt: new Date(event.timestamp * 1000) }
        : {}),
      role: 'system',
      content: [
        {
          type: 'text',
          text:
            readyAfter !== null
              ? `${label} in ${formatDuration(readyAfter)}`
              : label,
        },
      ],
      metadata: {
        custom: {
          kind: 'lifecycle',
          sequence: event.seq,
          lifecycleTimestamp: event.timestamp,
          loading,
          failed:
            event.status === 'error' ||
            (event.status === 'starting' &&
              events[index + 1]?.status === 'error'),
          lifecycleDetail:
            event.status === 'asleep'
              ? undefined
              : (presentation.detail ??
                reasonDetails[event.reason ?? ''] ??
                (loading
                  ? bridge.queue.length
                    ? 'Your message is saved. It will send automatically when the session is ready.'
                    : 'Preparing your workspace and connecting the agent…'
                  : undefined)),
        },
      },
    }
  })
  if (
    ['unknown', 'created', 'starting'].includes(bridge.status) &&
    (bridge.record || bridge.lifecycle.length || bridge.startup.length)
  ) {
    const phase = bridge.startup.at(-1)
    const start = bridge.lifecycle
      .filter((event) => ['created', 'starting'].includes(event.status))
      .at(-1)
    lifecycle.push({
      id: `lifecycle:startup:${start?.seq ?? 0}`,
      role: 'system',
      content: [
        { type: 'text', text: phase?.label ?? 'Preparing your session…' },
      ],
      metadata: {
        custom: {
          kind: 'lifecycle',
          sequence: start?.seq ?? 0,
          loading: true,
          startupPhase: phase?.phase ?? 'preparing',
        },
      },
    })
  }
  return [...messages, ...lifecycle].sort(
    (a, b) =>
      Number(a.metadata?.custom?.sequence ?? 0) -
      Number(b.metadata?.custom?.sequence ?? 0),
  )
}
